/**
 * Alerts Panel Component
 * Display issues and notifications
 */

import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Badge, Button } from '../ui';
import { AlertTriangle, Clock, MapPin, X, Bell, CheckCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface Alert {
  id: string;
  type: 'delay' | 'offline' | 'deviation' | 'sos' | 'info';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  driverUid?: string;
  driverName?: string;
  routeId?: string;
  timestamp: string;
  isRead: boolean;
}

interface AlertsPanelProps {
  alerts: Alert[];
  onDismiss?: (alertId: string) => void;
  onMarkRead?: (alertId: string) => void;
  onAlertClick?: (alert: Alert) => void;
  maxHeight?: string;
}

const SEVERITY_STYLES = {
  low: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500' },
  medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-500' },
  high: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-500' },
  critical: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-500' },
};

const TYPE_ICONS = {
  delay: Clock,
  offline: MapPin,
  deviation: MapPin,
  sos: AlertTriangle,
  info: Bell,
};

export default function AlertsPanel({
  alerts,
  onDismiss,
  onMarkRead,
  onAlertClick,
  maxHeight = '400px',
}: AlertsPanelProps) {
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const filteredAlerts = filter === 'unread'
    ? alerts.filter((a) => !a.isRead)
    : alerts;

  const unreadCount = alerts.filter((a) => !a.isRead).length;

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card padding="none" className="flex flex-col">
      <CardHeader className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Alerts</CardTitle>
            {unreadCount > 0 && (
              <Badge variant="danger" size="sm">
                {unreadCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant={filter === 'all' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button
              variant={filter === 'unread' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setFilter('unread')}
            >
              Unread
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent
        className="px-4 pb-4 overflow-y-auto flex-1"
        style={{ maxHeight }}
      >
        {filteredAlerts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
            <p className="text-sm">
              {filter === 'unread' ? 'No unread alerts' : 'No alerts'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAlerts.map((alert) => {
              const styles = SEVERITY_STYLES[alert.severity];
              const Icon = TYPE_ICONS[alert.type];

              return (
                <div
                  key={alert.id}
                  className={cn(
                    'p-3 rounded-lg border cursor-pointer transition-all',
                    styles.bg,
                    styles.border,
                    !alert.isRead && 'ring-1 ring-offset-1',
                    alert.severity === 'critical' && !alert.isRead && 'ring-red-400',
                    alert.severity === 'high' && !alert.isRead && 'ring-orange-400'
                  )}
                  onClick={() => {
                    onMarkRead?.(alert.id);
                    onAlertClick?.(alert);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Icon className={cn('h-5 w-5 mt-0.5 flex-shrink-0', styles.icon)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">
                          {alert.title}
                        </span>
                        <Badge
                          variant={
                            alert.severity === 'critical' || alert.severity === 'high'
                              ? 'danger'
                              : alert.severity === 'medium'
                              ? 'warning'
                              : 'default'
                          }
                          size="sm"
                        >
                          {alert.severity}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 mb-1">{alert.message}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {alert.driverName && (
                          <span>Driver: {alert.driverName}</span>
                        )}
                        <span>{formatTime(alert.timestamp)}</span>
                      </div>
                    </div>
                    {onDismiss && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDismiss(alert.id);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
